/**
 * Vertical deposits timeline for the client dashboard.
 *
 * Mixes the last 2 fulfilled deposits with the next 4 scheduled ones,
 * with a "Today" marker between them. The retention play is the
 * consistency / momentum bias: the client *sees* their recent
 * follow-through right above the upcoming commitments, framing the
 * next deposit as "the next in a streak you've been keeping" instead
 * of "an obligation."
 *
 * Visual: a vertical line down the left edge with one dot per entry.
 * Completed dots get a sage check; upcoming get the gold brand dot.
 * The "Today" marker is a neutral horizontal divider with a label.
 */
import { formatMoney } from '../../lib/datetime';
import { colors, gradients } from '../../styles/theme';
import type { TimelineEntry } from '../../services/client.dashboard.service';
import { readerLocale } from '../../i18n/language';


interface Props {
  entries: TimelineEntry[];
  /** Title for the section header. */
  title?: string;
}

const CADENCE_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  weekly: 'Weekly',
  one_time: 'One-time',
};

/** Return "Tomorrow" / "In N days" / "Today" / formal date for
 *  upcoming entries. Past entries always use a formal short date —
 *  "3 days ago" reads oddly next to a money amount. */
function formatRelative(iso: string, kind: 'completed' | 'upcoming'): string {
  const target = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tDay = new Date(target);
  tDay.setHours(0, 0, 0, 0);
  const days = Math.round((tDay.getTime() - today.getTime()) / (24 * 60 * 60 * 1000));

  if (kind === 'upcoming') {
    if (days <= 0) return 'Today';
    if (days === 1) return 'Tomorrow';
    if (days <= 7) return `In ${days} days`;
  }
  return target.toLocaleDateString(readerLocale(), { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function DepositsTimeline({ entries, title = 'Contribution schedule' }: Readonly<Props>): JSX.Element {
  // Find the index of the first 'upcoming' entry to inject the
  // "Today" divider above it. If there are no completed entries, the
  // divider goes at the top so the upcoming list still has its anchor.
  const firstUpcomingIdx = entries.findIndex((e) => e.kind === 'upcoming');

  return (
    <div
      className="rounded-2xl p-6"
      style={{
        background: colors.surface,
        border: `1px solid ${colors.border}`,
      }}
    >
      <div className="mb-5">
        <p className="text-base font-semibold" style={{ color: colors.ink }}>
          {title}
        </p>
        <p className="text-xs mt-0.5" style={{ color: colors.muted }}>
          Your recent contributions and what&apos;s coming up.
        </p>
      </div>

      {entries.length === 0 ? (
        <p className="text-sm italic py-6 text-center" style={{ color: colors.muted }}>
          No contribution plan yet.{' '}
          <a href="/client/fund#plan" className="not-italic font-semibold" style={{ color: colors.ink }}>
            Set one up →
          </a>
        </p>
      ) : (
        <ol className="relative" style={{ paddingLeft: 28 }}>
          {/* Vertical rail */}
          <span
            className="absolute top-2 bottom-2 w-px"
            style={{ left: 9, background: colors.border }}
            aria-hidden
          />

          {entries.map((entry, idx) => (
            <li key={entry.id}>
              {idx === firstUpcomingIdx && firstUpcomingIdx > 0 && (
                <TodayDivider />
              )}
              <TimelineRow entry={entry} />
            </li>
          ))}

          {/* If everything is upcoming and we never injected the
              divider above, show one at the very top so the user
              still gets the "from today onward" anchor. */}
          {firstUpcomingIdx === 0 && entries.length > 0 && (
            <li className="absolute" style={{ left: -28, top: -8, right: 0 }}>
              {/* Placeholder — divider already implied by being first */}
            </li>
          )}
        </ol>
      )}
    </div>
  );
}

// ─── Sub-components ─────────────────────────────────────────────────

function TodayDivider(): JSX.Element {
  return (
    <div className="relative my-3" style={{ marginLeft: -28 }}>
      <span
        className="absolute inline-flex items-center justify-center"
        style={{
          left: 0,
          top: 0,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: colors.canvas,
          border: `1px solid ${colors.border}`,
          color: colors.muted,
          fontSize: 10,
          fontWeight: 600,
        }}
        aria-hidden
      >
        •
      </span>
      <p
        className="text-xs uppercase tracking-wider font-semibold"
        style={{ paddingLeft: 32, color: colors.muted, lineHeight: '20px' }}
      >
        Today
      </p>
    </div>
  );
}

function TimelineRow({ entry }: Readonly<{ entry: TimelineEntry }>): JSX.Element {
  const isCompleted = entry.kind === 'completed';
  return (
    <div className="relative py-3" style={{ marginLeft: -28 }}>
      {/* Dot */}
      <span
        className="absolute inline-flex items-center justify-center"
        style={{
          left: 0,
          top: 16,
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: isCompleted ? colors.successBg : colors.surface,
          border: `2px solid ${isCompleted ? colors.success : colors.gold}`,
          color: isCompleted ? colors.success : colors.gold,
          fontSize: 11,
          fontWeight: 700,
          lineHeight: 1,
          boxShadow: isCompleted ? 'none' : `0 0 0 4px rgba(212, 168, 75, 0.12)`,
        }}
        aria-hidden
      >
        {isCompleted ? '✓' : ''}
      </span>

      <div className="flex items-center justify-between gap-3" style={{ paddingLeft: 32 }}>
        <div className="min-w-0">
          <p
            className="text-sm font-semibold truncate"
            style={{ color: isCompleted ? colors.inkSoft : colors.ink }}
          >
            {entry.product_name}
          </p>
          <p className="text-xs mt-0.5" style={{ color: colors.muted }}>
            {formatRelative(entry.date, entry.kind)}
            {entry.cadence ? ` · ${CADENCE_LABEL[entry.cadence] ?? entry.cadence}` : ''}
            {isCompleted ? ' · Completed' : ''}
          </p>
        </div>
        <div
          className="text-sm font-semibold whitespace-nowrap"
          style={{
            color: isCompleted ? colors.success : colors.ink,
            // Subtle gold gradient text for the next upcoming deposit
            // gives it slightly more pull than the rest.
            ...(entry.kind === 'upcoming'
              ? { backgroundImage: gradients.goldText, WebkitBackgroundClip: 'text', backgroundClip: 'text' }
              : {}),
          }}
        >
          {isCompleted ? '+' : ''}{formatMoney(entry.amount, entry.currency)}
        </div>
      </div>
    </div>
  );
}
